import * as PIXI from 'pixi.js';
import {Assets, Texture} from 'pixi.js';
import {GameObject} from "../base/GameObject";
import {PixiAssetsScaleFactor, AbstractPixiRoot} from "../AbstractPixiRoot";
import {LibraryFlump} from "../flump/library/LibraryFlump";
import {LibraryMold} from "../flump/library/LibraryMold";
import {LibrarySpine} from "../spine/LibrarySpine";
import {TextureAtlas} from "@esotericsoftware/spine-pixi-v8";
import {LibraryText} from "../text/LibraryText";

export interface AssetAlias {
  alias:string;
  src:string;
  isMultipack?:boolean;
}

export class PixiAssetsLoader extends GameObject {

  private static referenceCounter: Map<string, number> = new Map(); // Храним счетчик использования ассетов
  private loadedAssets: Set<string> = new Set(); // Отслеживаем загруженные ассеты для текущего экрана
  private onLoadCallbacks: (() => void)[] = [];
  private isLoading: boolean = false;
  private loadingQueue: AssetAlias[] = [];
  private flumpLibs: Map<string, LibraryFlump> = new Map();
  private spineLibs: Map<string, LibrarySpine> = new Map();
  private textLibs: Map<string, LibraryText> = new Map();
  private _isLoaded = false;

  readonly root: AbstractPixiRoot;

  constructor(root: AbstractPixiRoot) {
    super();
    this.root = root;
    if (!this.root.isInitialized) {
      throw new Error("PixiAssetsLoader requires initialized PIXI instance");
    }
  }

  get isLoaded() {
    return this._isLoaded;
  }

  private get assetsScaleFactorPath() {
    return `assets/${this.root.scaleFactor}x`;
  }

  // public getSpineLibraryByName(name: string): LibrarySpine | undefined {
  //   return this.spineLibraries.get(name);
  // }

  private get assetsFlumpDirPath() {
    return `assets/flump`;
  }

  private get assetsSpineDirPath() {
    return `assets/spine`;
  }

  public getTextLib(libName: string) {
    return this.textLibs.get(libName)!;
  }

  public getFlumpLib(libName: string) {
    return this.flumpLibs.get(libName)!;
  }

  public getSpineLib(libName: string) {
    return this.spineLibs.get(libName);
  }

  public async enqueueFontFace() {
    throw new Error('Not implemented');
    // for not bitmap text
    // let font:FontFace;
    // switch (Locale.currentLanguage) {
    //   case Locale.hi:
    //     font = new FontFace('main', 'url(/assets/fonts/Laila-Bold.ttf)');
    //     break;
    //   default:
    //     font = new FontFace('main', 'url(/assets/fonts/main.ttf)');
    //     break;
    // }
    // await font.load();
    // (document.fonts as FontFaceSet & { add(font: FontFace): void }).add(font);
  }

  public enqueueBitmapFont(fontFileName:string) {
    if (!this.textLibs.has(fontFileName)) {
      this.textLibs.set(fontFileName, new LibraryText(this.root, fontFileName));
      this.enqueueFile({src: 'assets/fonts/'+fontFileName+'.xml', alias: fontFileName});
    }
  }

  private static flumpLibJsonFilePrefix = 'flumpLib_';
  public enqueueFlumpLib(name: string, isMultiatlas = false, libInstance: LibraryFlump | null = null): void {
    if (!this.flumpLibs.has(name)) {
      this.flumpLibs.set(name, libInstance || new LibraryFlump(this.root));

      this.enqueueAtlas(name, isMultiatlas);
      const libFileAlias:AssetAlias = {alias:PixiAssetsLoader.flumpLibJsonFilePrefix+name, src: `${this.assetsFlumpDirPath}/${name}.json`};
      this.loadingQueue.push(libFileAlias);
      this.incrementReferenceCount(libFileAlias.src);

      this.loadedAssets.add(libFileAlias.alias);
      this.incrementReferenceCount(libFileAlias.alias); // Увеличиваем счетчик использования для каждого ресурса
    }
  }

  private static spineLibJsonFilePrefix = 'spineLib_';
  private static spineAtlasFilePrefix = 'spineAtlas_';
  public enqueueSpineLib(name: string): void {
    if (!this.spineLibs.has(name)) {
      const skeletonName = PixiAssetsLoader.spineLibJsonFilePrefix + name;
      const atlasName = PixiAssetsLoader.spineAtlasFilePrefix + name;
      this.spineLibs.set(name, new LibrarySpine(skeletonName, atlasName));

      let alias:AssetAlias = {alias:atlasName, src: `${this.assetsScaleFactorPath}/spine/${name}.atlas`};
      this.enqueueFile(alias);

      const libFileAlias:AssetAlias = {alias:skeletonName, src: `${this.assetsSpineDirPath}/${name}.json`};
      this.enqueueFile(libFileAlias);

      // spine assets hack
      this.loadedAssets.add(skeletonName + '-' + atlasName + '-1');
      this.incrementReferenceCount(skeletonName + '-' + atlasName + '-1');
    }
  }
  // !!! Make sure to only load the first sprite sheet of your multipack set.
  // If you load more than one sheet from the same set, PixiJS deadlocks and does not complete the loading process.
  // PIXI.Assets.load([
  //     "spritesheets/sheet-0.json",
  // ]).then(() => {
  public enqueueAtlas(name: string, isMultiatlas = false): void {
    let alias:AssetAlias;
    if (isMultiatlas) {
      alias = {alias:name, src: `${this.assetsScaleFactorPath}/${name}/${name}-0.json`, isMultipack: true};
      this.enqueueFile(alias);

      alias = {alias:name, src: `${this.assetsScaleFactorPath}/${name}/${name}-1.json`, isMultipack: true};
      this.enqueueFile(alias);
    } else {
      alias = {alias:name, src: `${this.assetsScaleFactorPath}/${name}/${name}.json`};
      this.enqueueFile(alias);
    }
  }

  public enqueueFile(alias: AssetAlias) {
    // for (const child of this.loadingQueue) {
    //   if (child.alias == alias.alias && child.src == alias.src) {
    //     return false;
    //   }
    // }
    this.loadingQueue.push(alias);
    this.loadedAssets.add(alias.src);
    this.incrementReferenceCount(alias.src);
    return true;
  }

  public async loadAssets(onLoad?: () => void): Promise<void> {
    if (this.isLoading) {
      if (onLoad) {
        this.onLoadCallbacks.push(onLoad);
      }
      return;
    }

    if (this._isLoaded) {
      if (onLoad) {
        onLoad();
      }
      return;
    }

    this.isLoading = true;

    try {
      // Загружаем ассеты для текущего экрана
      await PIXI.Assets.load(this.loadingQueue);

      // const cache = Assets.cache;

      this.flumpLibs.forEach((libraryFlump, flumpName) => {
        const libJSON = Assets.cache.get(PixiAssetsLoader.flumpLibJsonFilePrefix+flumpName);
        const libraryMold = LibraryMold.fromJSON(libJSON, AbstractPixiRoot.multiply(this.root.scaleFactor));
        libraryFlump.init(flumpName, libraryMold);
      });

      this._isLoaded = true;

      // console.log(this.loadedAssets);

      // Вызываем все колбэки загрузки
      for (const callback of this.onLoadCallbacks) {
        callback();
      }

      this.onLoadCallbacks = [];
      this.isLoading = false;

      if (onLoad) {
        onLoad();
      }
    } catch (error) {
      this.isLoading = false;
      this._isLoaded = false;
      console.error('Ошибка при загрузке ассетов:', error);
    }
  }

  private replaceFileNameInPath(path:string, fileName:string){
    let lastSlashIndex = path.lastIndexOf("/");
    return path.substring(0, lastSlashIndex + 1) + fileName;
  }

  public async unloadAssets() {
    if (!this._isLoaded) {
      return;
    }
    const loadedAssets = this.loadedAssets;
    for (const path of loadedAssets) {
      this.decrementReferenceCount(path); // Уменьшаем счетчик использования при удалении экрана
    }
    this.loadedAssets.clear();
    this._isLoaded = false;
  }

  public getTextureByName(alias: string): PIXI.Texture | undefined {
    const cache = Assets.cache;
    const value = cache.get(alias);
    return value as PIXI.Texture;
  }

  public unloadAssetByAlias(alias: string) {
    this.decrementReferenceCount(alias);
  }

  public getObjectByAlias(alias: string) {
    const cache = Assets.cache;
    const value = cache.get(alias);
    return value;
  }

  override destroy(): void {
    if (!this._isDestroyed) {
      this.unloadAssets();
      this.loadingQueue = [];
      this.onLoadCallbacks = [];
      this.textLibs.clear();
      this.flumpLibs.clear();
      this.spineLibs.clear();
    }
    super.destroy();
  }

  private incrementReferenceCount(path: string): void {
    const count = PixiAssetsLoader.referenceCounter.get(path) || 0;
    PixiAssetsLoader.referenceCounter.set(path, count + 1);
  }

  private async decrementReferenceCount(path: string) {
    const count = PixiAssetsLoader.referenceCounter.get(path) || 0;
    if (count > 0) {
      PixiAssetsLoader.referenceCounter.set(path, count - 1);
      if (count - 1 <= 0) {
        const cache = Assets.cache;
        const asset = cache.get(path);

        // Spine textures dispose
        if (asset instanceof TextureAtlas) {
          const atlas = asset as TextureAtlas;
          atlas.pages.forEach((page) => {
            if (page.texture) {
              // @ts-ignore
              const texture:any = page.texture['texture'] as any;
              if (texture instanceof Texture) {
                (texture as Texture).destroy(true);
              }
            }
            const image = page.texture?.getImage();
            if (image instanceof ImageBitmap) {
              image.close();
            }
            page.texture?.dispose();
          });
          atlas.dispose();
        }

        await PIXI.Assets.unload(path);

        cache.remove(path);
      }
    }
  }
}
